import {type FC, memo, useCallback, useEffect, useRef} from "react";

import {CloseIcon} from "../../react/components/Icons/CloseIcon";
import {SearchIcon} from "../../react/components/Icons/SearchIcon";
import type {InputRef} from "../ui/input";
import {Input, InputField, InputSlot} from "../ui/input";
import {Pressable} from "../ui/pressable";

type Props = {
    className?: string;
    query: string;
    testID?: string;
    placeholder?: string;
    autofocus?: boolean;
    onChangeText?: (text: string) => void;
    onClear: () => void;
    onSearch?: (text: string) => void;
    onBlur?: () => void;
};

const SearchInput: FC<Props> = ({
    className,
    query,
    testID,
    placeholder = "Search",
    autofocus,
    onSearch,
    onChangeText,
    onClear,
    onBlur
}) => {
    const innerRef = useRef<InputRef>(null);
    const inputWebRef = useRef<InputRef<"web">>(null);

    const handleKeyPress = useCallback(
        (event: React.KeyboardEvent<HTMLInputElement>) => {
            event.stopPropagation();
            if (event.key === "Enter") {
                event.preventDefault();
                onSearch?.(query);
            }
        },
        [query, onSearch]
    );

    const onClearInput = useCallback(() => {
        onClear();
        if (process.env.IS_WEB) {
            inputWebRef.current?.focus();
        }
    }, [onClear]);

    const handleFocus = useCallback(() => {
        if (process.env.IS_WEB) {
            inputWebRef.current?.setSelectionRange(query.length, query.length);
        }
    }, [query]);

    useEffect(() => {
        if (autofocus) {
            if (process.env.IS_WEB) {
                inputWebRef.current?.focus();
            } else {
                innerRef.current?.selectionState?.focus();
            }
        }
    }, [autofocus]);

    const elemLeft = (
        <InputSlot>
            <SearchIcon color="blueGray.400" />
        </InputSlot>
    );
    const elemRight = query ? (
        <InputSlot>
            <Pressable onPress={onClearInput}>
                <CloseIcon color="blueGray.400" size="xs" />
            </Pressable>
        </InputSlot>
    ) : undefined;

    if (process.env.IS_WEB) {
        // not sure if this remains correct?
        // adding `<form` helps to avoid the autofill somehow
        // setting `autoComplete="off"` doesn't work somehow
        return (
            <Input className={className} leftElement={elemLeft} rightElement={elemRight}>
                <InputField<"web">
                    ref={inputWebRef}
                    autoComplete="off"
                    className="min-w-0"
                    placeholder={placeholder}
                    testID={testID}
                    type="search"
                    value={query}
                    onBlur={onBlur}
                    onChangeText={onChangeText}
                    onFocus={handleFocus}
                    onKeyPress={handleKeyPress}
                />
            </Input>
        );
    }
    return (
        <Input className={className} leftElement={elemLeft} rightElement={elemRight}>
            <InputField
                ref={innerRef}
                autoComplete="off"
                className="min-w-0"
                placeholder={placeholder}
                testID={testID}
                type="search"
                value={query}
                onBlur={onBlur}
                onChangeText={onChangeText}
                onFocus={handleFocus}
            />
        </Input>
    );
};

export default memo(SearchInput);
